import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Cloud, Server, Code, Activity, Database, Award } from 'lucide-react';

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const skillCategories = [
    {
      title: 'Cloud',
      icon: Cloud,
      skills: [
        'AWS (Lambda, RDS, EC2, VPC, DMS, ECS, ELB, S3, Route53)',
        'Azure (AKS)'
      ]
    },
    {
      title: 'DevOps',
      icon: Server,
      skills: [
        'Kubernetes',
        'Rancher',
        'Docker',
        'Jenkins',
        'Terraform',
        'Ansible',
        'SonarQube'
      ]
    },
    {
      title: 'Programming',
      icon: Code,
      skills: ['Java', 'Python', 'Go', 'Bash']
    },
    {
      title: 'CI/CD',
      icon: Activity,
      skills: [
        'GitLab-CI',
        'Jenkins',
        'CodeBuild',
        'CodePipeline',
        'TeamCity',
        'Helm'
      ]
    },
    {
      title: 'Monitoring',
      icon: Activity,
      skills: [
        'Grafana',
        'New Relic',
        'Prometheus',
        'CloudWatch',
        'ELK Stack',
        'Nagios'
      ]
    },
    {
      title: 'Databases',
      icon: Database,
      skills: ['Redis', 'MySQL', 'PostgreSQL']
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="section-padding py-20 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="section-title text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-cyan-400 font-mono text-lg">03.</span> Skills & Technologies
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mb-12"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card
                  key={index}
                  className="glass-card bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-400/30 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10 group"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/20 group-hover:bg-cyan-500/20 transition-colors duration-300">
                        <IconComponent className="w-6 h-6 text-cyan-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {category.title}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {category.skills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-all duration-300"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Skills Section */}
          <Card className="glass-card bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-400/30 transition-all duration-300 mt-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/20">
                  <Award className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Scrum</h3>
              </div>
              <p className="text-gray-300">
                Registered Scrum Master by Scrum Inc.™
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Skills;